import { ChangesetConflictDTO, ResolutionChoices } from "../models/ChangeRequestDTO";
import { Fragment, useState } from "react";
import { Resolution, Resolutions, conflictKey } from "../utils/conflicts";

import { SideLabels } from "../utils/labels";
import { diffWordsWithSpace } from "diff";

/** Pages linked by name in the summary; the tree marks every one. */
const SummaryPages = 5;
/** Conflicts shown at first on a page; the rest behind "Show more". */
const FirstShown = 5;

const plural = (n: number, one: string, many: string) => (n === 1 ? `1 ${one}` : `${n} ${many}`);

interface ISummaryProps {
    conflicts: ChangesetConflictDTO[];
    /** How many of them the author has settled. */
    resolved: number;
    isAuthor: boolean;
    /** The author's name, for a reviewer's wording. */
    authorName?: string;
    /** True when an Update ran into them (not only the check): nothing was changed. */
    attempted: boolean;
    titleOf: (pageId: number) => string;
    onSelectPage: (pageId: number) => void;
    /** A reviewer who will not wait: reject with the conflicts as the reason. */
    onRejectWithConflicts?: () => void;
}

/**
 * The header's strip about the conflicts: how many, on which pages (each opens that page, where they are shown and resolved), and what
 * to do next. The details stay in the page pane so the header keeps its size whatever the count.
 */
export function RebaseConflictsSummary(props: ISummaryProps) {
    const counts = new Map<number, number>();
    for (const c of props.conflicts) counts.set(c.pageId, (counts.get(c.pageId) ?? 0) + 1);
    const pages = Array.from(counts.entries());
    const hidden = pages.length - SummaryPages;
    const left = props.conflicts.length - props.resolved;
    const blocked = props.conflicts.some((c) => c.code === "deleted" || c.code === "no-base");
    const author = props.authorName ?? "the author";
    return (
        <div className="review-strip strip-error" role="alert" aria-label="Conflicts">
            <div className="strip-body">
                <strong>
                    {props.attempted ? "Couldn't update: " : ""}
                    {plural(props.conflicts.length, "change conflicts", "changes conflict")} with edits made since
                </strong>
                {props.attempted ? ", nothing was changed" : ""}
                {": "}
                {pages.slice(0, SummaryPages).map(([pageId, n], i) => (
                    <span key={pageId}>
                        {i > 0 ? ", " : ""}
                        <button type="button" className="review-link" onClick={() => props.onSelectPage(pageId)}>{props.titleOf(pageId)}</button>
                        {pages.length > 1 || n > 1 ? ` (${n})` : ""}
                    </span>
                ))}
                {hidden > 0 ? ` and ${plural(hidden, "more page", "more pages")}` : ""}.
                {" "}
                {blocked
                    ? props.isAuthor
                        ? "Some can't be resolved here: withdraw this change request and propose it again from an up-to-date copy."
                        : `Some can't be resolved here: ${author} has to withdraw it and propose again from an up-to-date copy, or you reject it.`
                    : left === 0
                        ? `All resolved: Update merges the edits made since with your choices. Nothing changes until you do.${props.isAuthor ? "" : " It then counts as your update; the author sees what you decided."}`
                        : props.resolved > 0
                            ? `${plural(left, "conflict", "conflicts")} left: resolve ${left === 1 ? "it on its page" : "them on their pages"}, then Update.`
                            : props.isAuthor
                                ? "Pick a side for each on its page, or edit the lines, then Update."
                                : `Keep ${author === "the author" ? "the author's" : `${author}'s`} or take the page's on each page (or edit the lines), then Update; or reject with the conflicts as the reason.`}
            </div>
            <div className="strip-aside">
                {!blocked ? <span>{props.resolved} of {props.conflicts.length} resolved</span> : null}
                {!props.isAuthor && props.onRejectWithConflicts ? (
                    <button type="button" className="strip-button" onClick={props.onRejectWithConflicts}>Reject with these conflicts</button>
                ) : null}
            </div>
        </div>
    );
}

/** What the conflict is about: "Title", "Rule “status”", "Content · line 4", "Content · lines 143–172". */
function heading(c: ChangesetConflictDTO) {
    if (c.field === "title") return "Title";
    if (c.field === "rules") return c.path ? `Rule “${c.path}”` : "Rules";
    if (c.field === "content") {
        if (!c.line) return "Content";
        const lines = c.base ? c.base.split("\n").length : 0;
        return lines > 1 ? `Content · lines ${c.line}–${c.line + lines - 1}` : lines === 1 ? `Content · line ${c.line}` : `Content · at line ${c.line}`;
    }
    return "Page";
}

/** Past this many characters on the two sides together, a word diff is not worth its time: the value is shown plain. */
const MarkedLimit = 4000;

/** The value with the words that differ from the base marked (one mark across the spaces between changed words). */
export function Marked(props: { base: string; value: string }) {
    if (props.value === "") return <span className="conflict-empty">(empty)</span>;
    if (props.base.length + props.value.length > MarkedLimit) return <>{props.value}</>;
    const parts = diffWordsWithSpace(props.base, props.value).filter((p) => !p.removed);
    const runs: { text: string; changed: boolean }[] = [];
    parts.forEach((p, i) => {
        const bridge = !p.added && /^[ \t]+$/.test(p.value) && parts[i - 1]?.added && parts[i + 1]?.added;
        const changed = !!p.added || !!bridge;
        const last = runs[runs.length - 1];
        if (last && last.changed === changed) last.text += p.value;
        else runs.push({ text: p.value, changed });
    });
    return <>{runs.map((r, i) => (r.changed ? <mark key={i}>{r.text}</mark> : <Fragment key={i}>{r.text}</Fragment>))}</>;
}

/** Page-level conflicts in the reader's terms (the server's message names page ids). */
const pageMessages: Record<string, string> = {
    "changed-before-delete": "The page was edited after the proposal to delete it.",
    deleted: "The page was deleted after the proposal.",
    "no-base": "The page changed after the proposal, which is too old to merge automatically: withdraw it and propose again.",
};

/** The choices a conflict takes, in the reader's words. */
function choicesFor(c: ChangesetConflictDTO, labels: SideLabels): { choice: Resolution["choice"]; label: string; editable?: boolean }[] {
    if (c.code === "deleted" || c.code === "no-base") return [];
    if (c.field === "page") return [{ choice: ResolutionChoices.Ours, label: "Delete anyway" }];
    if (c.field === "content" && c.code === "changed-before-delete") {
        return [{ choice: ResolutionChoices.Ours, label: "Clear anyway" }, { choice: ResolutionChoices.Theirs, label: "Keep the page's content" }];
    }
    return [
        { choice: ResolutionChoices.Ours, label: labels.keepOurs },
        { choice: ResolutionChoices.Theirs, label: labels.takeTheirs },
        { choice: ResolutionChoices.Manual, label: "Edit", editable: true },
    ];
}

interface IConflictCardProps {
    conflict: ChangesetConflictDTO;
    resolution?: Resolution;
    canResolve: boolean;
    labels: SideLabels;
    onResolve: (resolution: Resolution | undefined) => void;
}

function ConflictCard(props: IConflictCardProps) {
    const { labels } = props;
    const c = props.conflict;
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(c.proposed ?? "");
    const hasValues = c.base != null || c.proposed != null || c.current != null;
    const base = c.base ?? "";
    const choices = props.canResolve ? choicesFor(c, labels) : [];
    const resolvedLabel = props.resolution
        ? c.field === "page" ? "deleted anyway"
            : c.field === "content" && c.code === "changed-before-delete"
                ? props.resolution.choice === ResolutionChoices.Ours ? "cleared anyway" : "the page's content kept"
                : labels.resolved[props.resolution.choice]
        : undefined;
    return (
        <li className={`conflict-card${props.resolution ? " resolved" : ""}`}>
            <div className="conflict-heading">
                <span>{heading(c)}</span>
                {props.resolution ? <span className="conflict-resolved">Resolved: {resolvedLabel}</span> : null}
                {props.canResolve ? (
                    <span className="merge-choices">
                        {props.resolution ? (
                            <button type="button" className="review-link" onClick={() => { props.onResolve(undefined); setEditing(false); }}>Change</button>
                        ) : editing ? null : choices.map((ch) => (
                            <button key={ch.choice} type="button" className="merge-choice"
                                onClick={() => (ch.editable ? (setDraft(c.proposed ?? ""), setEditing(true)) : props.onResolve({ choice: ch.choice }))}>
                                {ch.label}
                            </button>
                        ))}
                    </span>
                ) : labels.decides && !props.resolution && choicesFor(c, labels).length ? (
                    <span className="merge-decides">{labels.decides}</span>
                ) : null}
            </div>
            {editing ? (
                <div className="merge-edit">
                    {c.field === "title"
                        ? <input type="text" aria-label="Your title" value={draft} onChange={(ev) => setDraft(ev.target.value)} />
                        : <textarea aria-label={c.field === "rules" ? "Your rule value (JSON)" : "Your lines"} value={draft} rows={3} onChange={(ev) => setDraft(ev.target.value)} />}
                    <div className="merge-choices">
                        <button type="button" className="merge-choice" onClick={() => { props.onResolve({ choice: ResolutionChoices.Manual, value: draft }); setEditing(false); }}>
                            Use this
                        </button>
                        <button type="button" className="review-link" onClick={() => setEditing(false)}>Cancel</button>
                    </div>
                </div>
            ) : hasValues ? (
                <dl className="conflict-values">
                    <dt>{labels.ours}</dt>
                    <dd><pre><Marked base={base} value={c.proposed ?? ""} /></pre></dd>
                    <dt>{labels.theirs}</dt>
                    <dd><pre><Marked base={base} value={c.current ?? ""} /></pre></dd>
                    <dt className="conflict-was">Was</dt>
                    <dd className="conflict-was"><pre>{base === "" ? <span className="conflict-empty">(nothing)</span> : base}</pre></dd>
                    {props.resolution?.choice === ResolutionChoices.Manual ? (
                        <>
                            <dt>Edited</dt>
                            <dd><pre><Marked base={base} value={props.resolution.value ?? ""} /></pre></dd>
                        </>
                    ) : null}
                </dl>
            ) : (
                <div className="conflict-message">{pageMessages[c.code] ?? c.message}</div>
            )}
        </li>
    );
}

interface IPageConflictsProps {
    conflicts: ChangesetConflictDTO[];
    resolutions: Resolutions;
    /** The author of an open change request resolves; everyone else reads. */
    canResolve: boolean;
    onResolve: (key: string, resolution: Resolution | undefined) => void;
    labels: SideLabels;
}

/**
 * The conflicts of one page that are not line conflicts of its content (those are shown in the merged content): title, rules, a delete
 * or clear of a page edited since. Compact: what the author wrote, what the page has now (the words each changed marked), what both
 * started from, and the author's choice. The first few are shown, the rest on request.
 */
export function PageConflicts(props: IPageConflictsProps) {
    const [all, setAll] = useState(false);
    const shown = all ? props.conflicts : props.conflicts.slice(0, FirstShown);
    const rest = props.conflicts.length - shown.length;
    const resolved = props.conflicts.filter((c) => props.resolutions[conflictKey(c)]).length;
    return (
        <details className="review-field review-conflicts" open>
            <summary>
                <h4>
                    {plural(props.conflicts.length, "conflict", "conflicts")} with edits made since
                    {resolved ? ` · ${resolved} resolved` : ""}
                </h4>
            </summary>
            <ol>
                {shown.map((c) => {
                    const key = conflictKey(c);
                    return <ConflictCard key={key} conflict={c} resolution={props.resolutions[key]} canResolve={props.canResolve} labels={props.labels} onResolve={(r) => props.onResolve(key, r)} />;
                })}
            </ol>
            {rest > 0 ? (
                <button type="button" className="review-link conflict-more" onClick={() => setAll(true)}>Show {rest} more</button>
            ) : null}
        </details>
    );
}
